import { FC, ReactElement, SyntheticEvent, createElement, useEffect, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Box, SvgIcon, IconButton, Button, Typography } from "@mui/material";
import { PieChart, Close, QueryStats, Insights, Speed as SpeedIcon } from "@mui/icons-material";
import { TabContext, TabList } from "@mui/lab";
import { useSearchParams, Link as RouterLink } from "react-router-dom";
import { useSnackbar } from "notistack";

import { useAsu } from "@framework/provider-asu";
import { DEFAULT_TCI_LEVELS, ITCILevelResponse, ITCILevels, useSettings } from "@framework/provider-settings";
import { useApiCall } from "@framework/react-hooks";
import { useUser } from "@framework/provider-user";
import { IUser, mergeDashboardModulesOrder, sanitizeDashboardModulesOrder } from "@framework/types";

import JournalIcon from "../../assets/icons/journal.svg";
import EventsIcon from "../../assets/icons/events.svg";
import SchemeIcon from "../../assets/icons/scheme.svg";
import { TrainDetailsButton } from "../../components/common/buttons/train-details";
import { InfoDialogButton } from "../../components/common/buttons/info-dialog";
import { InformationTable } from "../../components/common/tables/information-table";
import { StyledTabWithTooltip } from "../../components/common/styled/tab";
import { DateRange } from "../../components/common/header/date-range";
import { Journal } from "./tabs/journal";
import { EventsLog } from "./tabs/events-log";
import { Scheme } from "./tabs/scheme";
import { Analytics } from "./tabs/analytics";
import { Forecasting } from "./tabs/forecasting";
import { MultiAnalysis } from "./tabs/multi-analysis";
import { Speed } from "./tabs/speed";
import { StyledWrapper, StyledTabPanel } from "./styled";
import {
  MAIN_DASHBOARD_MODULE_LABEL_MESSAGE_IDS,
  filterMainDashboardModuleIdsForVersion,
  getMainDashboardModuleIdsForVersion,
} from "./main-dashboard-modules-meta";
import { useFormatDateHumanReadable } from "../../hooks";

const MAIN_DASHBOARD_TAB_PANELS: Record<string, FC> = {
  "1": Scheme,
  "2": Journal,
  "3": EventsLog,
  "4": Analytics,
  "5": Forecasting,
  "6": MultiAnalysis,
  "7": Speed,
};

export const Dashboard: FC = () => {
  const { formatMessage } = useIntl();
  const formatDate = useFormatDateHumanReadable();
  const { setTCILevels } = useSettings();
  const [searchParams] = useSearchParams();
  const onlyPage = searchParams.get("onlyPage") === "true";
  const currentComponent = searchParams.get("component");
  const user = useUser<IUser>();
  const isAuthenticated = user.isAuthenticated();
  const passwordExpiresAt = user.profile.passwordExpiresAt;
  const interfaceSettings = user.profile.interfaceSettings;
  const availableDashboardModuleIds = useMemo(
    () => getMainDashboardModuleIdsForVersion(user.profile.version),
    [user.profile.version],
  );

  const visibleOrderedDashboardModuleIds = useMemo(() => {
    const orderIds = filterMainDashboardModuleIdsForVersion(
      sanitizeDashboardModulesOrder(interfaceSettings.dashboardModulesView ?? "", availableDashboardModuleIds),
      user.profile.version,
    );
    const vis = interfaceSettings.dashboardModulesVisibility ?? {};
    const filtered = orderIds.filter(id => vis[id] !== false);
    return filtered.length > 0 ? filtered : orderIds;
  }, [
    availableDashboardModuleIds,
    interfaceSettings.dashboardModulesView,
    interfaceSettings.dashboardModulesVisibility,
    user.profile.version,
  ]);

  const dashboardPanelIds = useMemo(
    () =>
      filterMainDashboardModuleIdsForVersion(
        mergeDashboardModulesOrder(
          sanitizeDashboardModulesOrder(interfaceSettings.dashboardModulesView ?? "", availableDashboardModuleIds),
          availableDashboardModuleIds,
        ),
        user.profile.version,
      ),
    [availableDashboardModuleIds, interfaceSettings.dashboardModulesView, user.profile.version],
  );

  const { tab, updateTab, selectedItem, getPasswordExpiryNotification } = useAsu();

  const tabAlignedWithVisibility = useMemo(() => {
    if (visibleOrderedDashboardModuleIds.includes(tab)) return tab;
    return visibleOrderedDashboardModuleIds[0] ?? tab;
  }, [tab, visibleOrderedDashboardModuleIds]);

  const resolvedTab =
    onlyPage && currentComponent === "stateAndForecast"
      ? visibleOrderedDashboardModuleIds.includes("5")
        ? "5"
        : tabAlignedWithVisibility
      : tabAlignedWithVisibility;
  const trainName = selectedItem && "TrainName" in selectedItem && selectedItem.TrainName;
  const stationName = selectedItem && "Name" in selectedItem && selectedItem.Name;

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const handleTabChange = (_event: SyntheticEvent, newValue: string) => {
    if (newValue === resolvedTab) {
      return;
    }

    updateTab(newValue);
  };

  const formatMainModuleTabLabel = (moduleId: string): string =>
    formatMessage(
      { id: MAIN_DASHBOARD_MODULE_LABEL_MESSAGE_IDS[moduleId] ?? "pages.dashboard.unknownMainModule" },
      { id: moduleId },
    );

  const renderDashboardModuleTab = (moduleId: string): ReactElement | null => {
    const isActiveModule = moduleId === resolvedTab;
    const label = formatMainModuleTabLabel(moduleId);
    const tooltip = !isActiveModule ? label : undefined;
    const caption = isActiveModule ? (
      <Typography component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
        {label}
      </Typography>
    ) : null;

    switch (moduleId) {
      case "1":
        return (
          <StyledTabWithTooltip
            key={moduleId}
            label={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <SvgIcon sx={{ mr: isActiveModule ? 1.5 : 0, width: 19, height: 19 }} viewBox="0 0 19 19">
                  <SchemeIcon />
                </SvgIcon>
                {caption}
              </Box>
            }
            tooltip={tooltip}
            value="1"
            disabled={isActiveModule}
          />
        );
      case "2":
        return (
          <StyledTabWithTooltip
            key={moduleId}
            label={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <SvgIcon sx={{ mr: isActiveModule ? 1.2 : 0, width: 18, height: 12 }} viewBox="0 0 18 12">
                  <JournalIcon />
                </SvgIcon>
                {caption}
              </Box>
            }
            tooltip={tooltip}
            value="2"
            disabled={isActiveModule}
          />
        );
      case "3":
        return (
          <StyledTabWithTooltip
            key={moduleId}
            label={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <SvgIcon sx={{ mr: isActiveModule ? 1.2 : 0, width: 20, height: 14 }} viewBox="0 0 20 14">
                  <EventsIcon />
                </SvgIcon>
                {caption}
              </Box>
            }
            tooltip={tooltip}
            value="3"
            disabled={isActiveModule}
          />
        );
      case "4":
        return (
          <StyledTabWithTooltip
            key={moduleId}
            label={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <SvgIcon sx={{ mr: isActiveModule ? 1.2 : 0, width: 20, height: 20 }} viewBox="0 0 24 24">
                  <PieChart />
                </SvgIcon>
                {caption}
              </Box>
            }
            tooltip={tooltip}
            value="4"
            disabled={isActiveModule}
          />
        );
      case "5":
        return (
          <StyledTabWithTooltip
            key={moduleId}
            label={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <SvgIcon sx={{ mr: isActiveModule ? 1.2 : 0, width: 20, height: 20 }} viewBox="0 0 24 24">
                  <QueryStats />
                </SvgIcon>
                {caption}
              </Box>
            }
            tooltip={tooltip}
            value="5"
            disabled={isActiveModule}
          />
        );
      case "6":
        return (
          <StyledTabWithTooltip
            key={moduleId}
            label={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <SvgIcon sx={{ mr: isActiveModule ? 1.2 : 0, width: 20, height: 20 }} viewBox="0 0 24 24">
                  <Insights />
                </SvgIcon>
                {caption}
              </Box>
            }
            tooltip={tooltip}
            value="6"
            disabled={isActiveModule}
          />
        );
      case "7":
        return (
          <StyledTabWithTooltip
            key={moduleId}
            label={
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <SvgIcon sx={{ mr: isActiveModule ? 1.2 : 0, width: 20, height: 20 }} viewBox="0 0 24 24">
                  <SpeedIcon />
                </SvgIcon>
                {caption}
              </Box>
            }
            tooltip={tooltip}
            value="7"
            disabled={isActiveModule}
          />
        );
      default:
        return (
          <StyledTabWithTooltip
            key={moduleId}
            label={<Box sx={{ display: "flex", alignItems: "center" }}>{caption}</Box>}
            tooltip={tooltip}
            value={moduleId}
            disabled={isActiveModule}
          />
        );
    }
  };

  const { fn: updateTCILevels } = useApiCall(
    async api => {
      try {
        const json = (await api.fetchJson({
          url: "/trains/levels",
        })) as ITCILevelResponse[];

        if (!json?.length) {
          return;
        }

        const levels: ITCILevels = {
          ...DEFAULT_TCI_LEVELS,
        };

        json.forEach(({ LevelID, LevelValue }) => {
          if (LevelID === 60) {
            levels.warning = LevelValue;
          }
          if (LevelID === 90) {
            levels.critical = LevelValue;
          }
        });

        setTCILevels(levels);
      } catch {}
    },
    { success: false, error: false },
  );

  useEffect(() => {
    void updateTCILevels();
  }, []);

  useEffect(() => {
    if (onlyPage) return;
    if (!visibleOrderedDashboardModuleIds.includes(tab)) {
      updateTab(visibleOrderedDashboardModuleIds[0] ?? tab);
    }
  }, [onlyPage, tab, updateTab, visibleOrderedDashboardModuleIds]);

  useEffect(() => {
    if (!passwordExpiresAt) return;

    const messageId = getPasswordExpiryNotification(passwordExpiresAt, 3);

    if (messageId)
      enqueueSnackbar(formatMessage({ id: `snackbar.${messageId}` }, { date: formatDate(passwordExpiresAt) }), {
        variant: "warning",
        persist: true,
        action: key => (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Button
              variant="outlined"
              size="small"
              color="inherit"
              component={RouterLink}
              to="/set-new-password"
              onClick={() => closeSnackbar(key)}
            >
              <FormattedMessage id="form.buttons.setNewPassword" />
            </Button>
            <IconButton size="small" color="inherit" onClick={() => closeSnackbar(key)}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        ),
      });
  }, [isAuthenticated, passwordExpiresAt]);

  return (
    <StyledWrapper sx={{ ...(onlyPage && { mt: 1 }) }}>
      <TabContext value={resolvedTab}>
        <Box sx={{ display: "flex", justifyContent: "space-between", height: "58px", mb: onlyPage ? 1 : 0 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: onlyPage ? "100%" : undefined,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", height: "48px", mb: onlyPage ? 0 : 1 }}>
              {trainName || stationName ? (
                <Typography
                  fontWeight={400}
                  fontSize={onlyPage ? "24px" : "28px"}
                  lineHeight="33px"
                  color="#363846"
                  sx={{ ml: 1 }}
                >
                  {trainName || stationName}
                </Typography>
              ) : null}
              {trainName ? <TrainDetailsButton /> : null}
              {stationName ? (
                <Box sx={{ ml: 1 }}>
                  <InfoDialogButton title={"pages.journals.popup.titleStation"}>
                    <InformationTable />
                  </InfoDialogButton>
                </Box>
              ) : null}
            </Box>
            {onlyPage && isAuthenticated && (
              <Box
                sx={{
                  backgroundColor: "white",
                  border: "1px solid #ebebf7",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  height: "58px",
                  position: "relative",
                  zIndex: 1100,
                }}
              >
                <DateRange />
              </Box>
            )}
          </Box>
          {onlyPage ? null : (
            <TabList
              onChange={handleTabChange}
              aria-label="journals tabs"
              sx={{ ml: 1, display: "flex", height: "48px", mb: 1 }}
              TabIndicatorProps={{ sx: { display: "none" } }}
            >
              {visibleOrderedDashboardModuleIds.map(renderDashboardModuleTab)}
            </TabList>
          )}
        </Box>

        {dashboardPanelIds.map((panelId: string) => (
          <StyledTabPanel key={panelId} value={panelId}>
            {MAIN_DASHBOARD_TAB_PANELS[panelId] ? (
              createElement(MAIN_DASHBOARD_TAB_PANELS[panelId])
            ) : (
              <Box sx={{ p: 2 }}>
                <Typography color="text.secondary">
                  <FormattedMessage id="pages.dashboard.unknownMainModulePanel" values={{ id: panelId }} />
                </Typography>
              </Box>
            )}
          </StyledTabPanel>
        ))}
      </TabContext>
    </StyledWrapper>
  );
};
